// ─────────────────────────────────────────────────────────────────────────────────────────────
// EVERY IN-APP ADDRESS, AND WHAT AN ADDRESS THAT IS NOT ONE SHOWS (moved out of App.js for
// fresh-eyes item 27, Carla, 2026-10-12).
//
// App.js read the page as PATH_TO_VIEW[pathname] || 'Dashboard', so a mistyped or retired address
// silently showed the Dashboard as if it had worked. viewForPath is now the one reading (start-up,
// Back/Forward, a notification link): a known address opens its page; an address something we send
// or link to still lands where it always has (ALIASES, each with its sender); anything else is
// NOT_FOUND, a "Page not found" page with a link to the Dashboard. The public pages (booking, forms,
// invoices, portals, contracts...) are answered by AppWrapper in App.js before any of this runs.
// nav/paths.test.js.
// ─────────────────────────────────────────────────────────────────────────────────────────────

export const NOT_FOUND = 'NotFound';

export const VIEW_TO_PATH = {
  Dashboard:       '/',
  Insights:        '/insights',
  Financials:      '/financials',
  Tasks:           '/tasks',
  Notifications:   '/notifications',   // Block 208 + merge: notifications AND the things needing a decision
  Today:           '/today',
  Tomorrow:        '/tomorrow',
  'Week View':     '/week',
  Notes:           '/notes',
  EmailDashboard:  '/emails',
  // '/email-settings' REMOVED 2026-10-04. The page collected eight settings — VIP
  // senders, trusted domains, auto-reply text, AI tone and length, custom
  // instructions, priority and urgency keywords — into localStorage, and EVERY reader
  // of them was unreachable: hooks/useEmailManager.js is imported by nobody, and
  // services/rulesEngine.js is imported only by that hook. Two of the eight were not
  // read even by the dead code. So a customer could configure VIP senders and an
  // auto-reply, and nothing in the product had ever consulted them.
  //
  // The triage that DOES work is server-side (routes/email/automation.js, 590 rows in
  // email_triage_cache) and takes no configuration at all. Whether triage should be
  // user-tunable is an open PRODUCT question, recorded in the plan with a size — not
  // a dead page.
  Invoices:        '/invoices',
  // 'Invoicing' was a second key here with no render block, so /invoicing and six
  // callers (Projects' Create invoice among them) opened an empty page. /invoicing
  // is now an alias in PATH_TO_VIEW below; the one view is 'Invoices'.
  AideyChat:       '/aidey',
  Contacts:        '/contacts',
  CRM:             '/crm',
  Forms:           '/forms',
  Workflows:       '/workflows',
  Proposals:       '/proposals',
  Contracts:       '/contracts',
  Scheduler:       '/scheduler',
  // Block 251 census (2026-09-10): the Scheduling hub's path was removed (its Event Types, Bookings
  // and Polls never reached the server). Block J (2026-10-12): what was left of it, the account's
  // default availability, is the Scheduler's Availability tab, and the hub itself is gone.
  ClientPortal:    '/portal',
  ProjectTracker:  '/projects',
  TeamDashboard:   '/team',
  TeamCalendar:    '/team/calendar',
  Calendar:        '/calendar',   // UI-4, decision 55: the Business calendar had no way in
  // Carla's item 8 (2026-10-12): Personal pages a notification opens (family chat, a reward or a
  // chore, a renewal). Without a path a push could only open the app's root.
  Family:          '/family',
  FamilyChat:      '/family-chat',
  Renewals:        '/renewals',
  Journal:         '/journal',   // build everything (2026-10-21): an address, so the chat can offer a button to it (journal photos need the file)
  TeamHR:          '/team/hr',
  TeamManagement:  '/team/management',
  Timesheet:       '/timesheet',
  Settings:        '/settings',
  AdminPanel:      '/admin',
  Profile:         '/profile',
  UiKit:           '/dev/ui-kit',   // UI-2: admin only, in no navigation
};

// Addresses that are not a page's own but must keep working: old pages that were merged, and
// addresses our own emails, redirects and links point at. Each says who sends people there.
export const ALIASES = {
  '/attention':  'Notifications',   // the old Attention Required page, merged into Notifications
  '/invoicing':  'Invoices',        // the old second key for Invoices
  '/index.html': 'Dashboard',
  '/dashboard':  'Dashboard',       // routes/billing.js Stripe success_url (/dashboard?subscribed=true)
  '/login':      'Dashboard',       // routes/auth.js and aidey-team.js sign-in links, Register, AcceptInvite
  '/settings/security': 'Settings', // routes/security.js redirects (?emailChanged / ?error)
  // Round 3, decision 10b (Carla, 2026-10-12): both are "choose or change your plan" links, so
  // both open Settings > Plan & billing (ALIAS_SECTIONS below). They had landed on the Dashboard.
  '/pricing':    'Settings',        // the billing emails' upgrade link (lib/emailTemplates, billing.js)
  '/billing':    'Settings',        // Family.js's "View plans" (upgrade to add more members)
  // Card at sign-up, phase 4: the billing emails' direct links (Visa 5-21: a simple online way to
  // cancel). Each opens Plan & billing with one thing open: Cancel plan, or Stripe's card form
  // (utils/cardUpdate billingIntentFromUrl reads which, before App rewrites the address).
  '/billing/cancel':      'Settings',
  '/billing/update-card': 'Settings',
  // routes/security.js's email-change link. Decision 10a: App.js's AppWrapper answers it first with
  // the VerifyEmailChange page (signed in or not); this entry only keeps it from reading as not found.
  '/verify-email-change': 'Dashboard',
  // Item 21 part 2: the old address's revert link; AppWrapper answers it with RevertEmailChange.
  '/revert-email-change': 'Dashboard',
};

// The Settings section an alias opens (SettingsLayout's initialSection). A section the person cannot
// see (Plan & billing is admin only) falls back to Settings' first section, as any deep link does.
export const ALIAS_SECTIONS = {
  '/pricing': 'billing',
  '/billing': 'billing',
  '/billing/cancel': 'billing',
  '/billing/update-card': 'billing',
};

export function settingsSectionForPath(pathname) {
  const p = String(pathname || '/');
  const clean = p.length > 1 ? (p.replace(/\/+$/, '') || '/') : p;
  return Object.prototype.hasOwnProperty.call(ALIAS_SECTIONS, clean) ? ALIAS_SECTIONS[clean] : null;
}

export const PATH_TO_VIEW = Object.fromEntries([
  ...Object.entries(VIEW_TO_PATH).map(([v, p]) => [p, v]),
  ...Object.entries(ALIASES),
]);

// Pages with an address per item (decision 90: "the address reflects it, so Back, reload and links
// work"): /notes/<id> is the Notes page, opened on that note (components/Notes/noteContent.js reads it).
// /contacts/<id> is the full contact page (Carla, 2026-10-21; components/Contacts/contactActions.js).
// ORDER: these are read only AFTER every literal address above (PATH_TO_VIEW), so a literal page
// under /contacts/ or /notes/ added later is never swallowed as an item id (the mount-order rule).
const ITEM_PATHS = [[/^\/notes\/[^/]+$/, 'Notes'], [/^\/contacts\/[^/]+$/, 'Contacts']];

// The page for an address: its view, or NOT_FOUND. A trailing slash is the same address.
export function viewForPath(pathname) {
  const p = String(pathname || '/');
  const clean = p.length > 1 ? (p.replace(/\/+$/, '') || '/') : p;
  if (Object.prototype.hasOwnProperty.call(PATH_TO_VIEW, clean)) return PATH_TO_VIEW[clean];
  const item = ITEM_PATHS.find(([re]) => re.test(clean));
  return item ? item[1] : NOT_FOUND;
}
